import { useEffect, useState } from "react";

import classnames from "classnames";
import { Button } from "neetoui";
import PropTypes from "prop-types";
import { not } from "ramda";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import SelectedLink from "components/Sidebar/Components/Links/SelectedLink";
import "components/Sidebar/sidebar.scss";

import Chevron from "./Sidebar/Components/Chevron";

const ConfigurePageSidebar = ({
  label,
  navLinks: items,
  configurePageRoute,
}) => {
  const history = useHistory();

  const [isSidebarPaneOpen, setIsSidebarPaneOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const { t } = useTranslation();

  const isLargerScreen = () => window.innerWidth > 768;
  const isSidebarClosed = isLargerScreen()
    ? isSidebarCollapsed
    : not(isSidebarPaneOpen);

  useEffect(() => {
    const handleResize = () => {
      if (isLargerScreen()) return;
      setIsSidebarPaneOpen(false);
      setIsSidebarCollapsed(true);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleConfigureLinkClick = () => {
    history.push(configurePageRoute);
  };

  return (
    <div
      data-testid="configure-page-sidebar-container"
      className={classnames(
        "neeto-molecules-sidebar__wrapper neeto-molecules-sidebar__wrapper__configure-page",
        {
          "neeto-molecules-sidebar__wrapper--collapsed": isLargerScreen()
            ? isSidebarCollapsed
            : not(isSidebarPaneOpen),
        }
      )}
    >
      <div
        className="neeto-molecules-sidebar__backdrop"
        onClick={() => {
          setIsSidebarPaneOpen(false);
        }}
      />
      <div
        className="neeto-molecules-sidebar neeto-molecules-sidebar__configure-page neeto-ui-flex neeto-ui-flex-col neeto-ui-flex-shrink-0 neeto-ui-overflow-y-auto neeto-ui-overflow-x-hidden neeto-molecules-sidebar--focus-mode"
        data-testid="configure-page-sidebar"
      >
        <SelectedLink
          isConfigureSidebar
          handleGoBack={handleConfigureLinkClick}
          navLink={{ label, items }}
        />
      </div>
      <Button
        className="neeto-molecules-sidebar__toggler"
        data-testid="configure-page-neeto-molecules-sidebar-toggler"
        icon={() => <Chevron />}
        style={isLargerScreen() ? "text" : "secondary"}
        tooltipProps={{
          content: isSidebarClosed
            ? t("neetoMolecules.sidebar.showMenu")
            : t("neetoMolecules.sidebar.hideMenu"),
          position: "bottom",
          touch: ["hold", 500],
        }}
        onClick={() => {
          setIsSidebarCollapsed(!isSidebarCollapsed);
          setIsSidebarPaneOpen(not);
        }}
      />
    </div>
  );
};

ConfigurePageSidebar.propTypes = {
  /**
   * The route to the landing page of the configure screen.
   * When the user clicks on the 'configure' text, they will be taken to the specified route.
   */
  configurePageRoute: PropTypes.string,
  /**
   * To specify the label to be shown on top of the sidebar.
   */
  label: PropTypes.string,
  /**
   * To specify the configure nav links to be shown in the sidebar.
   */
  navLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      to: PropTypes.string,
      items: PropTypes.arrayOf(
        PropTypes.shape({ label: PropTypes.string, to: PropTypes.string })
      ),
    })
  ),
};

export default ConfigurePageSidebar;
